import {
  ProcessedSearchResult,
  ProcessedSearchResults,
} from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { getDiscountTypeAndAmount } from '@app/utils/getPriceComponent';
import { InteractElementContext } from '@redbubble/next/types';
import {
  AnalyticsItemListID,
  AnalyticsItemsListIDNameMap,
  getSearchFilters,
  getSearchType,
  getTopicClickedLabel,
  getTopicIndex,
  Item,
  RbGtmClient,
} from '@redbubble/next-analytics/gtm';
import { ObjectValues } from 'types';
import { getDiscountValue, getExternalReferrer } from '../../../utils/common';
import { DEFAULT_PER_PAGE_RESULTS } from '../constants';
import { SearchResultsPageProps } from '../types';

type ParseSearchResultToGA4EcommerceItem = (
  result: ProcessedSearchResult,
  index: number,
  analyticsComponentID: AnalyticsItemListID
) => Item | null;

type InteractElement = {
  name: string;
  text?: string | null;
  url?: string | null;
};

export const InteractElementAction = {
  Click: 'click',
} as const;

type InteractElementActionType = ObjectValues<typeof InteractElementAction>;

export const parseSearchResultToAnalyticsItem: ParseSearchResultToGA4EcommerceItem =
  (
    result: ProcessedSearchResult,
    index: number,
    analyticsComponentID: AnalyticsItemListID
  ) => {
    if (!result || !result.inventoryItem) return null;
    const { inventoryItem } = result;
    const priceTypeAndAmount = getDiscountTypeAndAmount(
      inventoryItem.volumeDiscount?.thresholds || [],
      inventoryItem.price
    );

    if (!priceTypeAndAmount) return null;

    const { discountType, discountedAmount } = priceTypeAndAmount;
    const discountValue = getDiscountValue(inventoryItem.price);

    return {
      discount: discountValue,
      index: Number.isInteger(index) ? index : null,
      work_id: result.inventoryItem.work.id, // when this is expanted to handle extra inventory items we will need to handle workIds coming from inventoryItem.workId
      product_type_id: inventoryItem.productTypeId,
      blank_item_id: inventoryItem.blankItemId,
      inventory_item_id: inventoryItem.id, // the following or statement will be required for list pages || inventoryItem.inventoryItemId
      retargeting_id: inventoryItem.marketingProductTypeId,
      item_list_id: analyticsComponentID,
      item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
      item_id: inventoryItem.id, // the following or statement will be required for list pages || inventoryItem.inventoryItemId
      price: inventoryItem.price ? inventoryItem.price.amount : null,
      quantity: 1,
      display_price: discountedAmount,
      discount_type: discountType,
      custom_discount: discountValue,
    };
  };

export function trackGA4SRPViewItemListEvent(
  results: ProcessedSearchResults,
  analyticsComponentID: AnalyticsItemListID,
  topic: string | null,
  searchUUID: string | null,
  startIndex: number = 0 // current offset index if we are using a sliced set
) {
  const analyticsItems = results
    .map((result, index) =>
      parseSearchResultToAnalyticsItem(
        result,
        index + startIndex,
        analyticsComponentID
      )
    )
    .filter((item): item is NonNullable<Item> => item !== null);

  RbGtmClient.track({
    event: 'view_item_list',
    page_location: window.location.href,
    topic: topic || '',
    search_query_id: searchUUID,
    ecommerce: {
      item_list_id: analyticsComponentID,
      item_list_name: AnalyticsItemsListIDNameMap[analyticsComponentID],
      items: analyticsItems,
    },
  });
}

const DEFAULT_SORT_ORDER = 'relevant';

export const trackGA4ViewSearchResults = (
  bubblesData: SearchResultsPageProps['bubblesData'],
  category: SearchResultsPageProps['category'],
  formattedQuery: SearchResultsPageProps['formattedQuery'],
  searchPageType: SearchResultsPageProps['searchPageType'],
  pagination: SearchResultsPageProps['pagination'],
  relatedTopics: SearchResultsPageProps['relatedTopics'],
  resultCount: SearchResultsPageProps['resultCount'],
  searchContext: SearchResultsPageProps['searchContext'],
  searchUUID: SearchResultsPageProps['searchUUID'],
  topic: SearchResultsPageProps['topic']
) => {
  const urlSearchParams = new URLSearchParams(window.location.search);

  const sortOrderParam = urlSearchParams.get('sortOrder');
  const sortOrder =
    sortOrderParam !== null ? sortOrderParam : DEFAULT_SORT_ORDER;

  const totalResults = pagination?.total || resultCount;
  const resultsPerPage = pagination?.perPage || DEFAULT_PER_PAGE_RESULTS;
  const topicSuggestions = bubblesData
    ? bubblesData?.bubbles.map((item) => item.title).join(', ')
    : relatedTopics
        ?.map((relatedTopic) => relatedTopic?.title)
        .filter(Boolean)
        .join(', ');

  RbGtmClient.track({
    event: 'view_search_results',
    num_results_returned: totalResults,
    ia_category: searchContext?.category || '',
    search_term: formattedQuery,
    search_type: getSearchType(urlSearchParams, sortOrder),
    timestamp: `${Date.now() * 1000}`,
    search_query_id: searchUUID,
    search_id_external_referrer: getExternalReferrer(),
    search_topic: topic,
    search_product: category,
    search_query_type: searchPageType,
    page_number:
      (totalResults && totalResults > resultsPerPage
        ? pagination?.currentPage
        : 1) || null,
    page_size:
      totalResults && totalResults > resultsPerPage
        ? resultsPerPage
        : totalResults,
    search_filters: getSearchFilters(urlSearchParams),
    sort_order: sortOrder,
    topic_suggestions: topicSuggestions,
    topic_clicked_index: getTopicIndex(),
    topic_clicked_label: getTopicClickedLabel(),
  });
};

export const trackGA4InteractElement = ({
  item,
  context,
  action,
  isOutbound,
  isNav,
}: {
  item: InteractElement;
  context: InteractElementContext;
  action: InteractElementActionType;
  isOutbound: boolean;
  isNav: boolean;
}) => {
  RbGtmClient.track({
    event: 'interact_element',
    element: item.name,
    element_text: item.text ?? '',
    element_url: item.url ?? '',
    context: JSON.stringify(context),
    action,
    is_outbound: isOutbound,
    is_nav: isNav,
    page_location: window.location.toString(),
  });
};
